/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  SelectPageSize,
  StyledButtonGroups,
  StyledCard,
  TBody,
  THead,
  Table,
  TableWrapper,
} from '../../../../../components'
import {useRowTable} from '../../../../../hooks/useRowTable'
import {useAppDispatch, useAppSelector} from '../../../../../redux/hooks'
import {
  filterSelector,
  resetFilterLogQueue as resetFilter,
  setFilterLogQueue as setFilter,
  setIsFetchData,
} from '../../../../../redux/slices/filterSlices'
import {fetchLogQueue, headers} from '../../../../../services/fetcher'
import {IFetchLogResponse} from '../../../../../services/types'
import {objToParams} from '../../../../../utils'
import Pagination from '../../../component/Pagination'
import {RowData} from './RowData'
import {RowFilter} from './RowFilter'
import {RowHead} from './RowHead'
import { useTranslation } from 'react-i18next'

export const TableData = () => {
  const {filterLogQueue: filter} = useAppSelector(filterSelector)
  const dispatch = useAppDispatch()
  const {t} = useTranslation()

  const [page, setPage] = useState(0)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [tableData, setTableData] = useState([])
  const [numberURL, setNumberURL] = useState([])

  const rowNumber = useMemo(() => {
    const per = filter?.per_page ?? 5
    return page * per - per
  }, [page, filter])

  const populateState = (data: any) => {
    setTableData(data.data)
    setPage(data.current_page)
    setNumberURL(data.links)
  }

  const paginateNumber = async (url: string) => {
    if (!url) return

    try {
      setIsLoading(true)
      setIsError(false)

      const endpoint = `${url}&${objToParams(filter)}`
      const token = localStorage.getItem('token') as string
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })
      const data = res.data.data as IFetchLogResponse['data']
      populateState(data)
    } catch (error) {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const onFetchData = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)
    fetchLogQueue(filter)
      .then((res) => {
        const data = res.data.data
        populateState(data)
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsLoading(false)
        dispatch(setIsFetchData(false))
      })
  }, [filter, dispatch])

  const {RowEmptyData, RowErrorFetch, RowLoader} = useRowTable({
    colSpan: 7,
    onReloadTable: () => handleReloadTable(),
  })

  const handleReloadTable = () => {
    dispatch(resetFilter())
    onFetchData()
  }

  const handlePageSizeChange = (value?: number) => {
    dispatch(setFilter({...filter, per_page: value ?? 5}))
  }

  useEffect(() => {
    const getData = setTimeout(onFetchData, 1000)
    return () => clearTimeout(getData)
  }, [onFetchData])

  useEffect(() => {
    return () => {
      dispatch(resetFilter())
    }
  }, [])

  return (
    <StyledCard>
      <StyledButtonGroups
        leftContent={
          <SelectPageSize
            initialValue={filter?.per_page ?? 5}
            onChange={(value: any) => handlePageSizeChange(value?.value)}
          />
        }
        rightContent={
          <button
            className='btn btn-sm btn-primary'
            data-bs-toggle='tooltip'
            data-bs-placement='top'
            data-bs-trigger='/hover'
            title={t('reload table')}
            onClick={handleReloadTable}
          >
            <i className='fas fa-sync-alt'></i>
          </button>
        }
      />

      <TableWrapper>
        <Table>
          <THead>
            <RowHead />
          </THead>
          <TBody>
            <RowFilter />

            {isLoading ? (
              <RowLoader />
            ) : isError ? (
              <RowErrorFetch />
            ) : tableData.length === 0 ? (
              <RowEmptyData />
            ) : (
              <RowData reloadTable={handleReloadTable} items={tableData} rowNumber={rowNumber} />
            )}
          </TBody>
        </Table>
      </TableWrapper>

      <Pagination paginate={paginateNumber} test={numberURL} />
    </StyledCard>
  )
}
